import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Users, AlertCircle, ChevronRight, Trash2, KeyRound, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function AdminCustomers() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [resetDialog, setResetDialog] = useState(null); // {customer, tempPassword}
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/customers", { params: q ? { q } : {} });
      setList(data);
      setSelected(new Set());
    } finally { setLoading(false); }
  };
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/customers", { params: q ? { q } : {} });
        if (!active) return;
        setList(data);
        setSelected(new Set());
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [q]);

  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleAll = () => setSelected((s) => s.size === list.length ? new Set() : new Set(list.map((c) => c.id)));

  const deleteOne = async (c) => {
    if (!window.confirm(`Eliminare definitivamente il cliente "${c.name || ''} ${c.surname || ''}" (${c.email})? L'operazione è irreversibile e cancellerà anche le sue prenotazioni.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/customers/${c.id}`);
      toast.success("Cliente eliminato");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail || e.response?.statusText || e.message;
      toast.error(`Impossibile eliminare: ${formatApiError(detail) || detail}`);
    }
    finally { setBusy(false); }
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Eliminare definitivamente ${selected.size} clienti selezionati? L'operazione è irreversibile.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/customers/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${data.deleted_count} clienti eliminati`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const generateTempPassword = () => {
    const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let out = "";
    const cryptoObj = window.crypto || window.msCrypto;
    if (cryptoObj && cryptoObj.getRandomValues) {
      const buf = new Uint32Array(8);
      cryptoObj.getRandomValues(buf);
      for (let i = 0; i < 8; i++) out += alphabet[buf[i] % alphabet.length];
    } else {
      for (let i = 0; i < 8; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
    }
    return out;
  };

  const openResetDialog = (c) => {
    setCopied(false);
    setResetDialog({ customer: c, tempPassword: generateTempPassword(), confirmed: false });
  };

  const confirmReset = async () => {
    if (!resetDialog) return;
    setBusy(true);
    try {
      await api.post(`/admin/customers/${resetDialog.customer.id}/reset-password`, { new_password: resetDialog.tempPassword });
      setResetDialog((d) => ({ ...d, confirmed: true }));
      toast.success("Password temporanea impostata. Comunicala al cliente via WhatsApp.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const copyPassword = async () => {
    if (!resetDialog?.tempPassword) return;
    try {
      await navigator.clipboard.writeText(resetDialog.tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = resetDialog.tempPassword;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-customers-page">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#FFD700]">Anagrafica</p>
        <h1 className="font-display text-4xl font-bold mt-1">Clienti</h1>
        <p className="text-zinc-400 text-sm mt-1">Fascicolo digitale completo per ogni cliente registrato.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Cerca per nome, cognome, email, telefono, documento, patente…"
                 className="bg-zinc-900 border-zinc-800 pl-9"
                 data-testid="customers-search-input" />
        </div>
        {selected.size > 0 && (
          <Button onClick={deleteSelected} disabled={busy}
                  className="bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/40"
                  data-testid="customers-bulk-delete">
            <Trash2 className="w-4 h-4 mr-2" /> Elimina {selected.size} selezionati
          </Button>
        )}
      </div>

      <div className="border border-zinc-800 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900/40">
              <TableHead className="w-8">
                <Checkbox checked={list.length > 0 && selected.size === list.length}
                          onCheckedChange={toggleAll}
                          className="border-zinc-700 data-[state=checked]:bg-[#FFD700] data-[state=checked]:text-zinc-950 data-[state=checked]:border-[#FFD700]"
                          data-testid="customers-select-all" />
              </TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Cliente</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Contatti</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Documenti</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Patente</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Noleggi</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-zinc-500 text-center py-8">Caricamento…</TableCell></TableRow>
            ) : list.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-zinc-500 text-center py-12"><Users className="w-8 h-8 mx-auto mb-2 opacity-50" /> Nessun cliente</TableCell></TableRow>
            ) : (
              list.map((c) => {
                const missing = c.missing_documents || [];
                const totalRentals = (c.manual_contracts_count || 0) + (c.online_bookings_count || 0);
                const licExpiry = c.license_expiry_date ? new Date(c.license_expiry_date) : null;
                const licDays = licExpiry ? Math.ceil((licExpiry - new Date()) / 86400000) : null;
                const licCls = licDays == null ? "text-zinc-500" : licDays < 0 ? "text-red-400" : licDays <= 60 ? "text-amber-400" : "text-emerald-400";
                const isSel = selected.has(c.id);
                return (
                  <TableRow key={c.id} className={`border-zinc-800 hover:bg-zinc-900/20 ${isSel ? "bg-zinc-900/40" : ""}`} data-testid={`customer-row-${c.id}`}>
                    <TableCell>
                      <Checkbox checked={isSel} onCheckedChange={() => toggleOne(c.id)}
                                className="border-zinc-700 data-[state=checked]:bg-[#FFD700] data-[state=checked]:text-zinc-950 data-[state=checked]:border-[#FFD700]"
                                data-testid={`customer-select-${c.id}`} />
                    </TableCell>
                    <TableCell>
                      <p className="text-zinc-100 font-medium">{c.name || "—"} {c.surname || ""}</p>
                      <p className="text-zinc-500 text-xs font-mono">{c.document_number || "—"}</p>
                    </TableCell>
                    <TableCell className="text-xs">
                      <p className="text-zinc-300">{c.email}</p>
                      <p className="text-zinc-500">{c.phone || "—"}</p>
                    </TableCell>
                    <TableCell>
                      {missing.length === 0 ? <span className="text-xs text-emerald-400">✓ Completi</span> : (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-400"><AlertCircle className="w-3 h-3" />{missing.length}/4 mancanti</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {c.license_number ? (
                        <>
                          <p className="text-zinc-300 font-mono">{c.license_number}</p>
                          {licDays != null && <p className={licCls}>{licDays < 0 ? `Scaduta ${Math.abs(licDays)}g fa` : `${licDays}g`}</p>}
                        </>
                      ) : <span className="text-zinc-600">—</span>}
                    </TableCell>
                    <TableCell className="text-zinc-300 text-sm">
                      <span className="font-semibold">{totalRentals}</span>
                      <span className="text-zinc-500 text-xs ml-1">({c.manual_contracts_count || 0}M / {c.online_bookings_count || 0}O)</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1 items-center">
                        <Link to={`/admin/customers/${c.id}`} className="inline-flex items-center gap-1 text-[#FFD700] hover:underline text-sm" data-testid={`customer-open-${c.id}`}>
                          Fascicolo <ChevronRight className="w-3 h-3" />
                        </Link>
                        <Button size="sm" variant="ghost" onClick={() => openResetDialog(c)} className="text-blue-300 hover:text-blue-200 hover:bg-blue-500/10 h-7 px-2" title="Reset password" data-testid={`customer-reset-${c.id}`}>
                          <KeyRound className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteOne(c)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2" data-testid={`customer-delete-${c.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!resetDialog} onOpenChange={(v) => { if (!v) setResetDialog(null); }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#FFD700]" /> Reset Password Cliente
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {resetDialog?.confirmed
                ? "Comunica questa password temporanea al cliente via WhatsApp. Dovrà cambiarla al prossimo login."
                : `Verrà generata una password temporanea per ${resetDialog?.customer?.name || ""} ${resetDialog?.customer?.surname || ""} (${resetDialog?.customer?.email || ""}). Il cliente sarà obbligato a cambiarla al prossimo accesso.`}
            </DialogDescription>
          </DialogHeader>

          {resetDialog && (
            <div className="space-y-3 py-2">
              <div className="rounded-lg border border-[#FFD700]/40 bg-[#FFD700]/[0.05] p-4">
                <p className="text-xs uppercase tracking-widest text-[#FFD700] mb-1">Password temporanea</p>
                <div className="flex items-center justify-between gap-3">
                  <code className="text-2xl font-mono tracking-[0.3em] text-zinc-100" data-testid="temp-password-value">
                    {resetDialog.tempPassword}
                  </code>
                  <Button size="sm" variant="ghost" onClick={copyPassword} className="text-zinc-300 hover:text-white" data-testid="temp-password-copy">
                    {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              {!resetDialog.confirmed && (
                <p className="text-xs text-zinc-500">
                  Conferma per impostare questa password sull&apos;account. Da quel momento la vecchia password non sarà più valida.
                </p>
              )}
              {resetDialog.confirmed && (
                <p className="text-xs text-emerald-400">
                  ✓ Password impostata. Invia subito al cliente via WhatsApp ({resetDialog.customer.phone || "telefono assente"}).
                </p>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {!resetDialog?.confirmed ? (
              <>
                <Button variant="ghost" onClick={() => setResetDialog(null)} className="text-zinc-400" data-testid="reset-cancel">
                  Annulla
                </Button>
                <Button onClick={confirmReset} disabled={busy} className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold" data-testid="reset-confirm">
                  {busy ? "Imposto…" : "Conferma e imposta"}
                </Button>
              </>
            ) : (
              <Button onClick={() => setResetDialog(null)} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100" data-testid="reset-close">
                Chiudi
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
